import { CellSelection, TableMap, deleteRow } from "prosemirror-tables";
import { findTableNodePos } from "./prosemirror-table-utils";

/**
 * 표(tablePos)의 각 행(tbody > tr)의 렌더 높이(px)를 PM 행 순서대로 측정한다.
 * 열 삭제로 rowspan 병합 셀이 collapse될 때 높이를 보존하기 위해 쓴다.
 * 측정 불가 시 null(→ 호출부는 높이 보존을 건너뜀).
 */
function measureRowHeights(view: any, tablePos: number): number[] | null {
  try {
    const at = view?.domAtPos?.(tablePos + 1);
    let el: any = at?.node;
    if (el && el.nodeType === 3) el = el.parentElement;
    const tableEl: HTMLTableElement | null = el?.closest?.("table") ?? null;
    const body = tableEl?.tBodies?.[0];
    if (!body) return null;
    return Array.from(body.rows).map((tr) =>
      Math.round(tr.getBoundingClientRect().height),
    );
  } catch {
    return null;
  }
}

/**
 * 표에서 한 열(col)을 제거한 **새 table 노드를 재구성**해 교체하는 트랜잭션을 만든다.
 *
 * 왜 재구성인가:
 *  prosemirror-tables의 removeColumn(및 그를 쓰는 deleteColumn)은 열의 셀들을 개별
 *  tr.delete로 지운다. 그런데 BlockNote의 tableRow content는 `tableRow+`(셀 1개 이상
 *  필수)라, 어떤 행의 "유일한 셀"이 삭제 대상이면 ProseMirror가 행을 비울 수 없어 **빈
 *  셀 껍데기**를 남긴다. 인접 열이 rowspan 병합돼 있으면(예: 2·3열 세로병합 후 1열 삭제)
 *  이 빈 셀이 rowspan 셀과 충돌(collision)하고, tableEditing의 fixTables가 이를 원복해
 *  **열이 삭제되지 않는다**(코어 BlockNote에서도 재현되는 버그).
 *
 * 재구성 규칙:
 *  - col만 차지하는 셀(colspan 1) → 제거.
 *  - col을 가로질러 colspan>1로 걸친 셀 → colspan-1(및 colwidth 보정)로 유지.
 *  - 그 외 셀 → 그대로 유지.
 *  - 모든 원본 셀이 제거돼 **비게 되는 행**은 행 자체를 삭제하고, 그 행을 rowspan으로
 *    덮던 셀들의 rowspan을 줄인다.
 *  모든 셀/표 attr(병합, 배경색, verticalAlignment, rowHeight, tableAlignment 등)은
 *  노드를 그대로 재생성하므로 보존된다.
 *
 * @returns 적용할 transaction, 처리 불가/무변경 시 null.
 */
function buildDeleteColumnTr(
  state: any,
  tablePos: number,
  col: number,
  rowPx: number[] | null,
): any {
  const table = state.doc.nodeAt(tablePos);
  if (!table || table.type.name !== "table") return null;
  const map = TableMap.get(table);
  const W = map.width;
  const H = map.height;
  if (col < 0 || col >= W || W <= 1) return null;

  // 1. 고유 셀 + 원점(top,left)·span 수집.
  type CellInfo = {
    node: any;
    top: number;
    left: number;
    rowspan: number;
    colspan: number;
  };
  const cells: CellInfo[] = [];
  const seen = new Set<number>();
  for (let r = 0; r < H; r++) {
    for (let c = 0; c < W; c++) {
      const pos = map.map[r * W + c];
      if (seen.has(pos)) continue;
      seen.add(pos);
      const node = table.nodeAt(pos);
      if (!node) continue;
      cells.push({
        node,
        top: r,
        left: c,
        rowspan: node.attrs.rowspan ?? 1,
        colspan: node.attrs.colspan ?? 1,
      });
    }
  }

  // 2. 행별 "살아남는 원점 셀 수" 계산 → 0이면 그 행은 죽는다(삭제).
  const spansCol = (c: CellInfo) => c.left <= col && col < c.left + c.colspan;
  const isDropped = (c: CellInfo) => spansCol(c) && c.colspan === 1;
  const survivingByRow = new Array(H).fill(0);
  for (const c of cells) if (!isDropped(c)) survivingByRow[c.top]++;
  const rowDies = survivingByRow.map((n) => n === 0);
  const newRowIndex: number[] = [];
  let ni = 0;
  for (let r = 0; r < H; r++) newRowIndex[r] = rowDies[r] ? -1 : ni++;
  const newH = ni;
  if (newH === 0) return null; // 표 전체가 비는 경우는 처리하지 않음.

  // 3. 새 행 배열 구성(원점 셀을 left 순서대로 push).
  const newRows: any[][] = Array.from({ length: newH }, () => []);
  for (const c of cells) {
    if (isDropped(c)) continue;
    const newColspan = c.colspan - (spansCol(c) ? 1 : 0);
    let dyingWithin = 0;
    for (let r = c.top; r < c.top + c.rowspan; r++) if (rowDies[r]) dyingWithin++;
    const newRowspan = Math.max(1, c.rowspan - dyingWithin);

    const attrs: any = { ...c.node.attrs, colspan: newColspan, rowspan: newRowspan };
    if (spansCol(c) && Array.isArray(attrs.colwidth) && attrs.colwidth.length) {
      const cw = attrs.colwidth.slice();
      cw.splice(col - c.left, 1);
      attrs.colwidth = cw.some((w: number) => w > 0) ? cw : null;
    }
    // 높이 보존: rowspan이 줄어든(=빈 행이 제거된) 셀은 collapse로 키를 잃는다.
    // 원래 차지하던 행들의 렌더 높이 합을 rowHeight로 박아 시각 높이를 유지한다.
    if (dyingWithin > 0 && rowPx) {
      let sum = 0;
      let ok = true;
      for (let r = c.top; r < c.top + c.rowspan; r++) {
        if (typeof rowPx[r] !== "number") {
          ok = false;
          break;
        }
        sum += rowPx[r];
      }
      if (ok && sum > 0) attrs.rowHeight = Math.round(sum);
    }
    const target = newRowIndex[c.top];
    if (target >= 0) newRows[target].push(c.node.type.create(attrs, c.node.content));
  }

  // 4. 새 table 노드 생성 후 교체.
  const rowType = table.child(0)?.type;
  if (!rowType) return null;
  const rowNodes = newRows.map((rowCells) => rowType.create(null, rowCells));
  const newTable = table.type.create(table.attrs, rowNodes);

  const tr = state.tr;
  tr.replaceWith(tablePos, tablePos + table.nodeSize, newTable);
  return tr.docChanged ? tr : null;
}

/**
 * 행/열 삭제를 대상 표에서 직접(결정적으로) 수행한다.
 *
 * 배경(중요):
 *  - 커스텀 포커스 기반 그립 메뉴가 열리면 ProseMirror 선택이 셀 밖으로 빠질 수 있어,
 *    코어 `tableHandles.removeRowOrColumn`(선택/`view.tablePos` 의존)이 실패한다.
 *  - 인접 열에 rowspan 병합이 있으면 코어 deleteColumn이 첫 열을 못 지운다(위 참고).
 *
 * 삭제 대상 표는 (1) 컨트롤러가 노출한 포커스 표 id → (2) 선택 → (3) view.tablePos
 * 순으로 찾는다. 열 삭제는 위 재구성 방식(buildDeleteColumnTr)으로 병합 셀까지 안전하게
 * 처리하고, 행 삭제는 코어 deleteRow(행 전체를 한 번에 제거 → 빈-셀 문제 없음)를 쓴다.
 *
 * @returns 처리 성공 여부. 표를 못 찾으면 false(호출부가 코어 기본 동작으로 폴백).
 */
export function removeFocusedRowOrColumn(
  editor: any,
  index: number,
  direction: "row" | "column",
): boolean {
  const tiptap = editor?._tiptapEditor;
  if (!tiptap) return false;
  const { state } = tiptap;

  // 1) 컨트롤러가 노출한 포커스 표 id(가장 권위 있음).
  let tablePos = -1;
  const activeId = editor?.__lumirActiveTableId;
  if (activeId) {
    const p = findTableNodePos(tiptap, activeId);
    if (p >= 0 && state.doc.nodeAt(p)?.type.name === "table") tablePos = p;
  }
  // 2) 현재 선택을 감싸는 table.
  if (tablePos < 0) {
    const $from = state.selection.$from;
    for (let d = $from.depth; d > 0; d--) {
      if ($from.node(d).type.name === "table") {
        tablePos = $from.before(d);
        break;
      }
    }
  }
  // 3) 코어 hover 추적 위치로 폴백.
  if (tablePos < 0) {
    const vp = editor?.tableHandles?.view?.tablePos;
    if (typeof vp === "number" && state.doc.nodeAt(vp)?.type.name === "table") {
      tablePos = vp;
    }
  }
  if (tablePos < 0) return false;

  try {
    if (direction === "column") {
      const rowPx = measureRowHeights(tiptap.view, tablePos);
      const tr = buildDeleteColumnTr(state, tablePos, index, rowPx);
      if (!tr) return false;
      tiptap.view.dispatch(tr);
      return true;
    }
    // 행 삭제: 대상 표 기준 CellSelection 구성 후 코어 deleteRow.
    const tableInside = state.doc.resolve(tablePos + 1);
    const rowStart = state.doc.resolve(tableInside.posAtIndex(index) + 1);
    const cellPos = state.doc.resolve(rowStart.posAtIndex(0));
    const selState = state.apply(
      state.tr.setSelection(new CellSelection(cellPos)),
    );
    return deleteRow(selState, (tr: any) => tiptap.view.dispatch(tr));
  } catch {
    return false;
  }
}
